// ADMIN-only add of a Section 2 Additional Certification
// template. Creates an UnderwritingCertificationTemplate. Tab is derived from
// the analyst lane the Add button lives on (A2/A3/A4 → Financial/Legal/
// Business); Section is always 2. The admin types only the PascalCase Key; the
// server derives the full code <Fin|Leg|Bus>.S2.<Key>. The new template appears
// on every deal (including in-progress ones) on the next analyst-tab load — and
// it cannot be deleted (the table has no soft-delete). Mirrors
// <CertificationCatalogEditModal>.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useCreateCertificationTemplate } from "@/hooks/dealFlow/useDealFlow";
import { tabForAnalystLane } from "./certConfigs";
import type { CriteriaLane, DealFlowTab } from "@/api/dealFlow/types";

interface CertificationCatalogCreateModalProps {
  open: boolean;
  onClose: () => void;
  lane: CriteriaLane;
}

const TITLE_MAX = 255;
const SHORT_DESCRIPTION_MAX = 500;
const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

const TAB_LABEL: Record<DealFlowTab, string> = {
  Underwriting: "Underwriting",
  FinancialAnalysis: "A2 · Financial",
  LegalAnalysis: "A3 · Legal",
  BusinessAnalysis: "A4 · Business",
  Closing: "Closing",
};

const TAB_PREFIX: Partial<Record<DealFlowTab, string>> = {
  FinancialAnalysis: "Fin",
  LegalAnalysis: "Leg",
  BusinessAnalysis: "Bus",
};

export function CertificationCatalogCreateModal({
  open,
  onClose,
  lane,
}: CertificationCatalogCreateModalProps) {
  const toast = useToast();
  const create = useCreateCertificationTemplate();

  const tab = tabForAnalystLane(lane);
  const prefix = TAB_PREFIX[tab] ?? "";

  const [key, setKey] = useState("");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [requiredEvidence, setRequiredEvidence] = useState("");

  // Reset to a blank form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setKey("");
    setTitle("");
    setShortDescription("");
    setDescription("");
    setRequiredEvidence("");
  }, [open]);

  const trimmedKey = key.trim();
  const keyValid = KEY_PATTERN.test(trimmedKey);
  const fullCode = `${prefix}.S2.${trimmedKey || "<key>"}`;

  const titleValid = title.trim().length > 0 && title.trim().length <= TITLE_MAX;
  const shortValid =
    shortDescription.trim().length > 0 &&
    shortDescription.trim().length <= SHORT_DESCRIPTION_MAX;
  const descriptionValid = description.trim().length > 0;
  const requiredEvidenceValid = requiredEvidence.trim().length > 0;
  const formValid =
    keyValid && titleValid && shortValid && descriptionValid && requiredEvidenceValid;

  const onSave = async () => {
    try {
      const created = await create.mutateAsync({
        tab,
        key: trimmedKey,
        title: title.trim(),
        shortDescription: shortDescription.trim(),
        description: description.trim(),
        requiredEvidence: requiredEvidence.trim(),
      });
      toast.success(`${created.certificationCode} added. Appears on all deals.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add certification"
      description="Adds a new Section 2 certification that applies to every deal — including in-progress ones."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={!formValid || create.isPending}
          >
            {create.isPending ? "Adding…" : "Add certification"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-wrap items-center gap-space-2">
          <Badge status="draft">{fullCode}</Badge>
          <span className="text-caption text-gray-mid">
            {TAB_LABEL[tab]} · Section 2
          </span>
        </div>

        <FormField
          label="Key"
          required
          htmlFor="cert-create-key"
          helperText="PascalCase — letters and digits, starting with a letter (e.g. DscrComputed)."
        >
          <Input
            id="cert-create-key"
            value={key}
            invalid={trimmedKey.length > 0 && !keyValid}
            placeholder="DscrComputed"
            onChange={(e) => setKey(e.target.value)}
          />
        </FormField>

        <p className="text-caption text-gray-mid m-0">
          Full code: <span className="font-mono text-onyx">{fullCode}</span>
        </p>

        <FormField label="Title" required htmlFor="cert-create-title">
          <Input
            id="cert-create-title"
            value={title}
            maxLength={TITLE_MAX}
            invalid={title.length > 0 && !titleValid}
            onChange={(e) => setTitle(e.target.value)}
          />
        </FormField>

        <FormField
          label="Short description"
          required
          htmlFor="cert-create-short-description"
          helperText="Shows under the title on the certification list."
        >
          <Textarea
            id="cert-create-short-description"
            rows={2}
            value={shortDescription}
            maxLength={SHORT_DESCRIPTION_MAX}
            invalid={shortDescription.length > 0 && !shortValid}
            onChange={(e) => setShortDescription(e.target.value)}
          />
        </FormField>

        <FormField label="What you're certifying" required htmlFor="cert-create-description">
          <Textarea
            id="cert-create-description"
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>

        <FormField label="What you'll need to produce" required htmlFor="cert-create-required-evidence">
          <Textarea
            id="cert-create-required-evidence"
            rows={5}
            value={requiredEvidence}
            onChange={(e) => setRequiredEvidence(e.target.value)}
          />
        </FormField>

        <p className="text-caption text-gray-mid m-0 border border-border-light rounded-md p-space-3">
          This certification applies to every deal — including ones already in
          progress — and cannot be deleted. New items appear alphabetically by
          code on the section list.
        </p>
      </div>
    </Modal>
  );
}
